import type { ReactNode } from "react";

type Align = "left" | "right" | "center";

const alignCls: Record<Align, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

export type MiniColumn<Row> = {
  header: ReactNode;
  /** Both the header and the cells (default left). */
  align?: Align;
  cell: (row: Row) => ReactNode;
};

/**
 * A small read-only table for a PANEL — a handful of rows beside a form, not
 * an index page.
 *
 * `Table` is the index-page surface: a sticky header, a scrolling body, a
 * full-height mode. All of that is weight a five-row list in a side column
 * cannot use, and its sticky header would pin against the wrong scroll
 * container. This is the same rows-under-a-header shape with none of it.
 *
 * Hairlines are drawn by the ROWS, so a table that ends early ends on a row
 * rather than on a rule hanging in space. Columns keep a gutter between them
 * (none after the last), so adjacent cells never run together.
 */
export function MiniTable<Row>({
  columns,
  rows,
  rowKey,
  empty = "Nothing yet.",
  ariaLabel,
  rowClassName,
}: {
  columns: readonly MiniColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  /** Said in place of the table when there are no rows. */
  empty?: ReactNode;
  /** Names the table for anyone who cannot see it; the header cells alone
   *  say what the columns are, not what the list IS. */
  ariaLabel: string;
  /** Extra classes for a row, from the row: dim a deleted one. */
  rowClassName?: (row: Row) => string;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-fg-muted">{empty}</p>;
  }
  return (
    <table className="w-full text-sm" aria-label={ariaLabel}>
      <thead>
        <tr className="border-b border-line">
          {columns.map((column, i) => (
            <th
              key={i}
              className={`label py-1.5 pr-4 !text-fg-subtle last:pr-0 ${alignCls[column.align ?? "left"]}`}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={rowKey(row)}
            className={`border-b border-line last:border-0 ${rowClassName?.(row) ?? ""}`.trim()}
          >
            {columns.map((column, i) => (
              <td
                key={i}
                className={`py-1.5 pr-4 last:pr-0 ${alignCls[column.align ?? "left"]}`}
              >
                {column.cell(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
